import { useEffect, useId, useState } from "react";
import { X } from "lucide-react";

let hsScriptLoaded = false;
let hsScriptPromise: Promise<void> | null = null;

declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (opts: Record<string, unknown>) => void;
      };
    };
  }
}

function loadHubSpotScript(portalId: string, region: string): Promise<void> {
  if (hsScriptLoaded) return Promise.resolve();
  if (hsScriptPromise) return hsScriptPromise;

  hsScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://js-${region}.hsforms.net/forms/embed/v2.js`;
    script.async = true;
    script.onload = () => {
      hsScriptLoaded = true;
      resolve();
    };
    script.onerror = () => {
      hsScriptPromise = null;
      reject(new Error("Failed to load HubSpot script"));
    };
    document.head.appendChild(script);
  });

  return hsScriptPromise;
}

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  incentive?: string;
  formId: string;
  portalId?: string;
  region?: string;
}

const FormModal = ({ isOpen, onClose, title, subtitle, incentive, formId, portalId = "26698741", region = "eu1" }: FormModalProps) => {
  const [scriptReady, setScriptReady] = useState(hsScriptLoaded);
  const [loadError, setLoadError] = useState(false);
  const containerId = useId().replace(/:/g, "");

  useEffect(() => {
    if (!isOpen) return;
    if (scriptReady) return;

    let cancelled = false;

    loadHubSpotScript(portalId, region)
      .then(() => {
        if (!cancelled) {
          setScriptReady(true);
          setLoadError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, scriptReady, portalId, region]);

  useEffect(() => {
    if (!isOpen || !scriptReady || !window.hbspt) return;

    const target = `#${containerId}`;
    const container = document.querySelector(target);
    if (!container) return;

    container.innerHTML = "";

    window.hbspt.forms.create({
      region,
      portalId,
      formId,
      target,
    });
  }, [isOpen, scriptReady, formId, portalId, region, containerId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="relative w-full max-w-lg md:max-w-2xl rounded-xl border-2 border-primary/30 bg-card p-4 md:p-6 shadow-[0_0_40px_-10px_hsl(var(--primary)/0.3)] ring-1 ring-primary/10 my-auto max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl md:text-2xl font-extrabold text-foreground mb-2 tracking-tight pr-6">
          {title}
        </h2>
        {subtitle && (
          <p className="text-muted-foreground mb-1 text-sm md:text-base">
            {subtitle}
          </p>
        )}
        {incentive && (
          <p className="text-xs text-primary font-semibold mb-4">
            {incentive}
          </p>
        )}

        {loadError ? (
          <div className="py-8 text-sm text-muted-foreground">
            The form could not load right now. Please close this window and try again.
          </div>
        ) : scriptReady ? (
          <div id={containerId} />
        ) : (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground animate-pulse">
            Loading form...
          </div>
        )}
      </div>
    </div>
  );
};

export default FormModal;